import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import {
  useDeployConfigurationSettingsMutation,
  useGetConfigurationSettingsPromptQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import * as yup from 'yup';
import { AddWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/AddWorkflowPopup/AddWorkflowPopup';
import { WorkflowsTable } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsTable';
import { API_VERSION } from 'src/constants/api-routes';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import { EditWorkflowBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/EditWorkflowBlock/EditWorkflowBlock';
import { useCreationFlowSettings } from 'src/pages/ProjectPage/CreateProjectSteps/useCreationFlowSettings';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import { formResolver } from 'src/utils/formResolver';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import { getApiVersion } from 'src/utils/apiVersion';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-52'} />
    </div>
  );
};

const CreateProjectStep7Page = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [customWorkflows, setCustomWorkflows] = useState<Workflow[]>(
    inputData?.pf_custom_workflows || [],
  );
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const { isEditWorkflowMode, setIsEditWorkflowMode } = useConfigurationContext();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;
  const dispatch = useAppDispatch();
  const { settings, activeSettings, isLoadingAllConfigurationSettings } = useCreationFlowSettings(
    id,
    { skip: !id },
  );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings?.id },
      {
        skip: !(id && activeSettings),
        refetchOnMountOrArgChange: true,
      },
    );
  const [deployConfigurationSettings, { isLoading: isLoadingDeployConfigurationSettings }] =
    useDeployConfigurationSettingsMutation();

  useEffect(() => {
    if (
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      isLoadingDeployConfigurationSettings ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
    settings,
  ]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (inputData) {
      methods.reset({
        pf_custom_workflows: inputData?.pf_custom_workflows || [],
      });

      setCustomWorkflows(inputData?.pf_custom_workflows || []);
    }
  }, [inputData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          finished: true,
        }),
      );

      deployConfigurationSettings({ project_id: id, settings_id: activeSettings.id }).then(
        (deployConfigurationSettingsResult) => {
          if (!('error' in deployConfigurationSettingsResult)) {
            next();
          }
        },
      );
    }
  }, [updateConfigurationSettingsData]);

  const methods = useForm<{ pf_custom_workflows: Workflow[] }>({
    defaultValues: {
      pf_custom_workflows: inputData?.pf_custom_workflows || [],
    },
    resolver: formResolver(
      yup.lazy(() => {
        const baseSchema = {
          pf_custom_workflows: array(
            object({
              name: stringScheme({ required: false }),
              description: stringScheme({ required: false }),
            }),
          ),
        };

        return yup.object(baseSchema);
      }),
    ),
  });

  // Workflows are validated when added or edited in their popups.
  const submit = () => {
    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          pf_custom_workflows: customWorkflows,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const onWorkflowsChange = (data: Workflow[]) => {
    setCustomWorkflows(data);
    methods.setValue('pf_custom_workflows', data);
  };

  const onWorkflowAdd = (workflow?: Workflow) => {
    if (!workflow) {
      return;
    }

    const newCustomWorkflows = [...customWorkflows, workflow];

    setCustomWorkflows(newCustomWorkflows);
    methods.setValue('pf_custom_workflows', newCustomWorkflows);
  };

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/control-desk?type=${ProductsType.customer}`
        : `${PATH_DASHBOARD.ROOT}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <CreateProjectStepsTitle step={isV1 ? +step : +step - 1} title={'Custom Workflows'} />
          <CreateProjectHead step={+step} titleIndex={7} />
          <div className={'flex flex-col items-center w-[455px] gap-5'}>
            <FormProvider {...methods}>
              <form
                id={'create-project-step-7'}
                className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
                onSubmit={methods.handleSubmit(submit)}
              >
                {isEditWorkflowMode ? (
                  <EditWorkflowBlock
                    onWorkflowAdd={onWorkflowAdd}
                    onWorkflowsChange={(workflow, index) => {
                      const newWorkflows = [...customWorkflows];
                      newWorkflows[index] = workflow;

                      onWorkflowsChange(newWorkflows);
                    }}
                    compact={true}
                  />
                ) : (
                  <>
                    <WorkflowsTable
                      isCustom={true}
                      workflows={customWorkflows}
                      readonly={false}
                      compact={true}
                      onChange={(workflow) => onWorkflowsChange(workflow)}
                      rowClassName={'bg-white hover:bg-white-800'}
                    />
                    {isV1 ? (
                      <AddWorkflowPopup
                        onSubmit={onWorkflowAdd}
                        trigger={
                          <Button size={'xl'} variant={'purple'} className={'font-semibold'}>
                            Add Workflow
                          </Button>
                        }
                      />
                    ) : (
                      <Button
                        onClick={(event) => {
                          event.preventDefault();
                          setIsEditWorkflowMode(true);
                        }}
                        size={'xl'}
                        variant={'purple'}
                        className={'font-semibold'}
                      >
                        Add Workflow
                      </Button>
                    )}
                  </>
                )}
                <Button
                  size={'xl'}
                  form="create-project-step-7"
                  type="submit"
                  className="w-full flex flex-row h-12 mt-5 font-semibold"
                  disabled={!isV1 && isEditWorkflowMode}
                >
                  Continue
                </Button>
                <button
                  type="button"
                  onClick={back}
                  className={
                    'text-[#3F60B3] text-xs text-center font-medium cursor-pointer hover:underline'
                  }
                >
                  ← Back
                </button>
              </form>
            </FormProvider>
          </div>
        </>
      )}
    </PageContentWrapper>
  );
};

export default CreateProjectStep7Page;
